import { FieldValues } from 'react-hook-form';
import { FilterSidebarLayoutBase } from './FilterSidebarLayoutBase';
import { FilterSidebarLayoutContent } from './FilterSidebarLayoutContent';
import { FilterSidebarLayoutProps } from './types';

/**
 * Layout for creating views with filter sidebar
 * @description The layout's filter values are driven by supplied Zod schema which are persisted to URL on submit:
 * @param props.filterSchema - schema for the filter form
 * @param props.defaultFilterValues - default values for the filter form
 * @param props.queryParamsSchema - schema for the query parameters (may or may not include part(s) of the filter schema), can be omitted if it is the same as filter schema
 * @param props.defaultQueryParams - default (constant) query parameter values
 * @description Other props:
 * @param props.children - main content rendered to the left of the filter sidebar, can also be passed as render props function whose argument are the default and submitted filter values
 * @param props.formProps - props object passed to useForm hook, resolver is omitted since it will be created with the supplied Zod schema
 * @param props.filterSidebar.title - title component for the filter sidebar
 * @param props.filterSidebar.fields - react-hook-form fields for the filter form
 * @param props.filterSidebar.actions - additional secondary buttons rendered to the left of the submit button
 * @param props.submitButtonLabel - label for submit button, by default it is localized value of "ViewReport"
 * @param props.onFilterSubmit - callback function called with submitted filter values on successful filter form submission
 * @param props.loading - boolean flag to show loading spinner on submit button
 * @param props.withOverlayLoader - boolean flag to show spinner overlay over the entire layout
 * @description Also takes ModalHeader props
 */
/** @deprecated Use NewFilterSidebarLayout instead */
export const FilterSidebarLayout = <F extends FieldValues, Q = {}>({
    children,
    formProps = {},
    filterSchema,
    defaultFilterValues,
    queryParamsSchema,
    defaultQueryParams,
    filterSidebar,
    onFilterSubmit,
    submitButtonLabel,
    loading,
    withOverlayLoader,
    withClearFilters,
    toggleAfterSubmit,
    ...other
}: FilterSidebarLayoutProps<F, Q>) => (
    <FilterSidebarLayoutBase {...other} loading={withOverlayLoader && loading}>
        {(sidebarToggleProps) => (
            <FilterSidebarLayoutContent
                {...sidebarToggleProps}
                withClearFilters={withClearFilters}
                toggleAfterSubmit={toggleAfterSubmit}
                formProps={formProps}
                filterSchema={filterSchema}
                defaultFilterValues={defaultFilterValues}
                queryParamsSchema={queryParamsSchema}
                defaultQueryParams={defaultQueryParams}
                filterSidebar={filterSidebar}
                submitButtonLabel={submitButtonLabel}
                onFilterSubmit={onFilterSubmit}
                loading={loading}
            >
                {children}
            </FilterSidebarLayoutContent>
        )}
    </FilterSidebarLayoutBase>
);
